import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Gift, ArrowRight, Sparkles } from "lucide-react";

const PRESETS = [50, 100, 250, 500, 1000];

export default function GiftVouchers() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({
    purchaser_name: "", purchaser_email: "",
    recipient_name: "", recipient_email: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const activeAmount = custom ? Number(custom) : amount;

  const buy = async (e) => {
    e.preventDefault();
    if (!form.purchaser_name || !form.purchaser_email) { toast.error("Your name and email are required"); return; }
    if (!activeAmount || activeAmount < 25 || activeAmount > 2000) { toast.error("Amount must be £25 – £2000"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/gift-vouchers/checkout", {
        amount: activeAmount,
        origin_url: window.location.origin,
        purchaser_name: form.purchaser_name,
        purchaser_email: form.purchaser_email,
        recipient_name: form.recipient_name || undefined,
        recipient_email: form.recipient_email || undefined,
        message: form.message || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="gift-vouchers-page" className="bg-[#F5EFE4]">
      <section className="relative pt-32 pb-16 bg-[#1A1815] overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1522255272218-7ac5249be344?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1815] via-[#1A1815]/85 to-[#1A1815]/40" />
        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
          <p className="overline">Gift Vouchers</p>
          <h1 className="font-serif text-5xl lg:text-7xl mt-4 leading-[1.02] text-[#F5EFE4]">
            The <em className="italic text-[#C5A059]">Luxor gift.</em>
          </h1>
          <div className="mt-4 flex justify-center"><span className="divider-gold-center" /></div>
          <p className="mt-6 max-w-xl mx-auto text-[#F5EFE4]/70">
            Redeemable across every Luxor service and membership. Delivered instantly by email — printable, gift-ready.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="bg-white border border-[#E4D9C1] p-8 sticky top-24" data-testid="gift-preview">
            <div className="w-12 h-12 rounded-full bg-[#C5A059] text-white flex items-center justify-center"><Gift size={20} /></div>
            <p className="overline mt-6">Voucher preview</p>
            <div className="mt-6 p-8 bg-[#F5EFE4] border border-[#E4D9C1] text-center">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">Luxor Gift Voucher</p>
              <p className="font-serif text-6xl text-[#1A1815] mt-3">£{activeAmount || 0}</p>
              <p className="text-xs text-[#6B665E] mt-2">for {form.recipient_name || "someone extraordinary"}</p>
            </div>
            <ul className="mt-6 text-sm text-[#4A453E] space-y-2">
              <li className="flex gap-2"><Sparkles size={13} className="text-[#C5A059] mt-1" /> Instant email delivery + PDF-ready print page</li>
              <li className="flex gap-2"><Sparkles size={13} className="text-[#C5A059] mt-1" /> Redeem against any service or membership</li>
              <li className="flex gap-2"><Sparkles size={13} className="text-[#C5A059] mt-1" /> Valid 12 months from issue</li>
            </ul>
          </div>
        </div>

        <form onSubmit={buy} className="lg:col-span-7 bg-white border border-[#E4D9C1] p-8" data-testid="gift-form">
          <p className="overline">Choose amount</p>
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => { setAmount(p); setCustom(""); }} data-testid={`gift-preset-${p}`}
                className={`p-3 border text-center transition-colors ${amount === p && !custom ? "border-[#C5A059] bg-[#C5A059]/10 text-[#8B6D2E]" : "border-[#E4D9C1] hover:border-[#C5A059]/50"}`}>
                <div className="font-serif text-2xl">£{p}</div>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <label className="luxor-label">Or a custom amount (£25 – £2000)</label>
            <input type="number" min={25} max={2000} step={1} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. 750" className="luxor-field" data-testid="gift-custom-amount" />
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><label className="luxor-label">Your name *</label><input value={form.purchaser_name} onChange={(e) => setForm({ ...form, purchaser_name: e.target.value })} className="luxor-field" data-testid="gift-purchaser-name" required /></div>
            <div><label className="luxor-label">Your email *</label><input type="email" value={form.purchaser_email} onChange={(e) => setForm({ ...form, purchaser_email: e.target.value })} className="luxor-field" data-testid="gift-purchaser-email" required /></div>
            <div><label className="luxor-label">Recipient name</label><input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="luxor-field" data-testid="gift-recipient-name" /></div>
            <div><label className="luxor-label">Recipient email</label><input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="luxor-field" data-testid="gift-recipient-email" placeholder="Optional — sends to recipient too" /></div>
            <div className="md:col-span-2"><label className="luxor-label">Personal message</label><textarea rows={3} maxLength={300} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="luxor-field resize-none" data-testid="gift-message" /></div>
          </div>

          <button type="submit" disabled={loading} className="luxor-btn-primary w-full mt-8 justify-center disabled:opacity-50" data-testid="gift-checkout">
            {loading ? "Redirecting…" : (<>Buy £{activeAmount || 0} voucher <ArrowRight size={14} /></>)}
          </button>
          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.24em] text-[#6B665E]">
            Secure payment · Card · Apple Pay · Google Pay
          </p>
        </form>
      </section>
    </div>
  );
}
